import { useState, useEffect, useRef, useCallback } from 'react';
import type { PresenceStatus, ManualPresenceEntry, ResolvedPresenceStatus } from '../types/presence';
import type { WorkingSchedule } from '../types/database';
import { usePresence as useServerPresence, usePresenceActions } from '../contexts/PresenceContext';
import { usePreferences } from '../features/preferences/PreferencesContext';
import { isWithinWorkingSchedule, resolvePresenceTimeZone } from '../presence/derivePresence';

const STORAGE_KEY = 'qs_manual_presence_cache';
const INACTIVITY_MS = 5 * 60 * 1000; // 5 minutes

/** Read the localStorage cache, respecting expiry. */
function readCache(): ManualPresenceEntry | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const entry: ManualPresenceEntry = JSON.parse(raw);
    if (entry.expiresAt && new Date(entry.expiresAt) < new Date()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return entry;
  } catch {
    return null;
  }
}

export interface UsePresenceOptions {
  /**
   * The signed-in user. When given, the server's automatic statuses for them
   * (in a meeting, on site) feed the result, so the account menu shows what
   * everyone else sees.
   */
  userId?: string;
  /** users.timezone, the fallback when the Preferences time zone is 'auto'. */
  profileTimeZone?: string | null;
  /**
   * Initial manual entry loaded from the user record in the DB.
   * This is the authoritative value — it overrides the localStorage cache.
   */
  serverManualEntry?: ManualPresenceEntry | null;
  /** Current call status string from useCall(). */
  callStatus?: string;
  /** Call availability flag from useCall(). */
  callAvailability?: string;
  /** User's configured working hours schedule. */
  workingSchedule?: WorkingSchedule | null;
  /**
   * Called whenever the manual entry changes so the consumer can persist it
   * to the server (e.g. via DataService.updateUser).
   */
  onPersist?: (entry: ManualPresenceEntry | null) => void;
}

export interface UsePresenceReturn {
  /** The resolved presence status (auto or manual). */
  status: ResolvedPresenceStatus;
  /** Whether the status was explicitly set by the user. */
  source: 'auto' | 'manual';
  /** The stored manual entry, if any. */
  manualEntry: ManualPresenceEntry | null;
  /**
   * Set a manual presence override.
   * Pass null to clear and return to auto-detection.
   * Pass expiresAt to auto-reset after a duration.
   */
  setManualStatus: (status: PresenceStatus | null, expiresAt?: Date) => void;
}

/**
 * Resolves the current user's presence with the following priority chain:
 *
 * 1. Active phone call              → busy
 * 2. Manual override (if set)       → whatever the user chose
 * 3. Server automatic status        → in_meeting / on_site / away (out of office)
 * 4. DND from the call system       → dnd
 * 5. No activity for 5 min          → away
 * 6. Outside configured work hours  → away (re-evaluated every minute), only
 *    with Preferences > Availability > After hours on
 * 7. Default                        → available
 *
 * Steps 3 and 6 follow the same settings and time zone the server uses for
 * everyone else's view (src/presence/derivePresence.ts).
 *
 * Manual status is written to the server via onPersist and cached in
 * localStorage so the dot updates instantly on the current device while
 * the DB write completes in the background.
 */
export function usePresence({
  userId,
  profileTimeZone,
  serverManualEntry,
  callStatus,
  callAvailability,
  workingSchedule,
  onPersist,
}: UsePresenceOptions = {}): UsePresenceReturn {
  const { preferences } = usePreferences();
  const autoAfterHours = preferences.availability.autoAfterHours;
  const timeZone = resolvePresenceTimeZone(
    preferences.region.timeZone,
    profileTimeZone,
    workingSchedule?.timezone,
  );

  // ── Server automatic status for this user ─────────────────────────────────
  const { watchUsers } = usePresenceActions();
  useEffect(() => (userId ? watchUsers([userId]) : undefined), [userId, watchUsers]);
  const serverPresence = useServerPresence(userId);
  const serverAutoState: ResolvedPresenceStatus | null =
    serverPresence?.source === 'out_of_office' || serverPresence?.source === 'meeting' || serverPresence?.source === 'on_site'
      ? serverPresence.state ?? null
      : null;

  // ── Manual entry ──────────────────────────────────────────────────────────
  // Initialise from the server value when available, fall back to local cache.
  const [manual, setManualState] = useState<ManualPresenceEntry | null>(() => {
    if (serverManualEntry !== undefined) {
      if (serverManualEntry?.expiresAt && new Date(serverManualEntry.expiresAt) < new Date()) {
        return null;
      }
      return serverManualEntry ?? null;
    }
    return readCache();
  });

  // Sync when the server value arrives or changes (e.g. after the user record loads).
  const prevServerRef = useRef(serverManualEntry);
  useEffect(() => {
    if (prevServerRef.current === serverManualEntry) return;
    prevServerRef.current = serverManualEntry;
    if (serverManualEntry === undefined) return; // not yet loaded
    const valid = serverManualEntry?.expiresAt
      ? new Date(serverManualEntry.expiresAt) > new Date()
      : true;
    setManualState(valid ? (serverManualEntry ?? null) : null);
  }, [serverManualEntry]);

  // ── Inactivity detection ──────────────────────────────────────────────────
  const [isInactive, setIsInactive] = useState(false);
  const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetInactivity = useCallback(() => {
    setIsInactive(false);
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => setIsInactive(true), INACTIVITY_MS);
  }, []);

  useEffect(() => {
    const EVENTS = [
      'mousemove', 'keydown', 'mousedown',
      'touchstart', 'scroll', 'click',
    ] as const;
    EVENTS.forEach(ev => document.addEventListener(ev, resetInactivity, { passive: true }));
    resetInactivity();
    return () => {
      EVENTS.forEach(ev => document.removeEventListener(ev, resetInactivity));
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    };
  }, [resetInactivity]);

  // ── Minute-boundary ticker (for working hours re-evaluation) ─────────────
  const [, setMinuteTick] = useState(0);
  useEffect(() => {
    if (!autoAfterHours) return;
    const bump = () => setMinuteTick(n => n + 1);
    const now = new Date();
    const msToNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds();
    let interval: ReturnType<typeof setInterval>;
    const timeout = setTimeout(() => {
      bump();
      interval = setInterval(bump, 60_000);
    }, msToNextMinute);
    return () => {
      clearTimeout(timeout);
      if (interval) clearInterval(interval);
    };
  }, [autoAfterHours]);

  // ── Auto-expire manual entry ──────────────────────────────────────────────
  useEffect(() => {
    if (!manual?.expiresAt) return;
    const ms = new Date(manual.expiresAt).getTime() - Date.now();
    if (ms <= 0) {
      setManualState(null);
      localStorage.removeItem(STORAGE_KEY);
      onPersist?.(null);
      return;
    }
    const t = setTimeout(() => {
      setManualState(null);
      localStorage.removeItem(STORAGE_KEY);
      onPersist?.(null);
    }, ms);
    return () => clearTimeout(t);
  }, [manual?.expiresAt, onPersist]);

  // ── Setter ────────────────────────────────────────────────────────────────
  const onPersistRef = useRef(onPersist);
  useEffect(() => { onPersistRef.current = onPersist; }, [onPersist]);

  const setManualStatus = useCallback(
    (status: PresenceStatus | null, expiresAt?: Date) => {
      const entry: ManualPresenceEntry | null = status
        ? { status, expiresAt: expiresAt?.toISOString() ?? null, setAt: new Date().toISOString() }
        : null;

      // Update in-memory immediately
      setManualState(entry);

      // Write to localStorage as a fast cache for this device
      if (entry) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }

      // Persist to DB for cross-device sync
      onPersistRef.current?.(entry);
    },
    [],
  );

  // ── Status resolution (priority chain) ───────────────────────────────────
  const status: ResolvedPresenceStatus = (() => {
    // 1. Active call always wins
    if (
      callStatus === 'connected' ||
      callStatus === 'outgoing'  ||
      callStatus === 'voicemail' ||
      callStatus === 'incoming'
    ) return 'busy';

    // 2. Manual override
    if (manual) return manual.status;

    // 3. Server automatic status (meeting, on site)
    if (serverAutoState) return serverAutoState;

    // 4. DND from call system
    if (callAvailability === 'dnd') return 'dnd';

    // 5. Inactivity → away
    if (isInactive) return 'away';

    // 6. Outside working hours → away (re-checked on every minute tick)
    if (autoAfterHours && !isWithinWorkingSchedule(new Date(), workingSchedule ?? null, timeZone)) {
      return 'away';
    }

    // 7. Default
    return 'available';
  })();

  return {
    status,
    source: manual ? 'manual' : 'auto',
    manualEntry: manual,
    setManualStatus,
  };
}
